/**
 * Shape-preserving monotone cubic (PCHIP) resampling for stacked area charts.
 *
 * Why this exists: recharts draws a stacked <Area> layer as (top spline −
 * bottom spline), two INDEPENDENT curves through different cumulative point
 * sets. With type="monotone" the difference of those two monotone splines is
 * NOT itself overshoot-free, so a sparse extreme spike makes a layer bulge
 * sideways and bleed over its neighbours in the wrong colour. The cure is to
 * pre-sample each layer's OWN series with PCHIP (which provably never
 * overshoots — each hump stays inside its real [year_i, year_{i+1}] window)
 * and then render the dense points with type="linear". Linear over dense PCHIP
 * samples reproduces the smooth PCHIP curve exactly, with no cross-layer
 * artefact.
 *
 * PCHIP = piecewise cubic Hermite with Fritsch–Carlson tangents. It passes
 * through every input point exactly, so the original year knots survive intact
 * (load-bearing: tooltip/click snap the hovered x back to an integer year).
 */

export type ResampleRow = Record<string, number | null>;

/**
 * Fritsch–Carlson monotone tangents for the data (xs strictly increasing).
 * Guarantees the resulting Hermite interpolant is monotone on each segment and
 * never overshoots beyond the segment's endpoint values.
 */
export function monotoneCubicSlopes(xs: number[], ys: number[]): number[] {
  const n = xs.length;
  if (n === 0) return [];
  if (n === 1) return [0];

  const h: number[] = new Array(n - 1);
  const delta: number[] = new Array(n - 1);
  for (let i = 0; i < n - 1; i++) {
    h[i] = xs[i + 1] - xs[i];
    delta[i] = (ys[i + 1] - ys[i]) / h[i];
  }

  const m: number[] = new Array(n);
  // Interior tangents: 0 at local extrema / sign change, else weighted harmonic
  // mean of the two neighbouring secants (Fritsch–Carlson).
  for (let i = 1; i < n - 1; i++) {
    if (delta[i - 1] * delta[i] <= 0) {
      m[i] = 0;
    } else {
      const w1 = 2 * h[i] + h[i - 1];
      const w2 = h[i] + 2 * h[i - 1];
      m[i] = (w1 + w2) / (w1 / delta[i - 1] + w2 / delta[i]);
    }
  }

  // Endpoints: non-centred three-point estimate, clamped to preserve shape.
  m[0] = endpointSlope(h[0], h[1] ?? h[0], delta[0], delta[1] ?? delta[0]);
  m[n - 1] = endpointSlope(
    h[n - 2],
    h[n - 3] ?? h[n - 2],
    delta[n - 2],
    delta[n - 3] ?? delta[n - 2],
  );

  return m;
}

function endpointSlope(hEnd: number, hNext: number, dEnd: number, dNext: number): number {
  let slope = ((2 * hEnd + hNext) * dEnd - hEnd * dNext) / (hEnd + hNext);
  // Don't let the endpoint introduce a wiggle: clamp to the secant's sign and
  // to 3× its magnitude (standard PCHIP boundary monotonicity guard).
  if (slope * dEnd <= 0) {
    slope = 0;
  } else if (dEnd * dNext <= 0 && Math.abs(slope) > 3 * Math.abs(dEnd)) {
    slope = 3 * dEnd;
  }
  return slope;
}

/** Hermite basis evaluation on a unit interval (t in [0,1]). */
function hermite(p0: number, p1: number, m0: number, m1: number, hSeg: number, t: number): number {
  const t2 = t * t;
  const t3 = t2 * t;
  const h00 = 2 * t3 - 3 * t2 + 1;
  const h10 = t3 - 2 * t2 + t;
  const h01 = -2 * t3 + 3 * t2;
  const h11 = t3 - t2;
  return h00 * p0 + h10 * hSeg * m0 + h01 * p1 + h11 * hSeg * m1;
}

const num = (v: number | null | undefined): number => (typeof v === 'number' ? v : 0);

/**
 * Resample `rows` to `stepsPerYear` sub-points per segment using PCHIP, for the
 * given numeric `keys`. The original knots are preserved exactly; intermediate
 * `year` values are fractional. Each key is interpolated independently, so signs
 * are preserved (the Haushaltstechnik negative half stays ≤ 0).
 *
 * Rows must be sorted ascending by `year`. With fewer than 2 rows, or fewer than
 * 2 steps, the input is returned unchanged.
 */
export function pchipResample(
  rows: ResampleRow[],
  keys: string[],
  stepsPerYear: number,
): ResampleRow[] {
  if (rows.length < 2 || stepsPerYear < 2 || keys.length === 0) return rows;

  const xs = rows.map(r => num(r.year));
  // Per-key tangents, computed once.
  const slopes: Record<string, number[]> = {};
  const values: Record<string, number[]> = {};
  for (const key of keys) {
    const ys = rows.map(r => num(r[key]));
    values[key] = ys;
    slopes[key] = monotoneCubicSlopes(xs, ys);
  }

  const out: ResampleRow[] = [];
  const n = xs.length;
  for (let i = 0; i < n - 1; i++) {
    const hSeg = xs[i + 1] - xs[i];
    // s = 0 emits the left knot exactly; the final right knot is pushed after
    // the loop so knots are never duplicated.
    for (let s = 0; s < stepsPerYear; s++) {
      const t = s / stepsPerYear;
      const row: ResampleRow = { year: xs[i] + t * hSeg };
      for (const key of keys) {
        row[key] = hermite(values[key][i], values[key][i + 1], slopes[key][i], slopes[key][i + 1], hSeg, t);
      }
      out.push(row);
    }
  }
  // Final knot (exact).
  const last: ResampleRow = { year: xs[n - 1] };
  for (const key of keys) last[key] = values[key][n - 1];
  out.push(last);

  return out;
}
